import React, { useMemo, useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { AppRegion, buildRegionLoginUrl, getCurrentRegion, getRegionDisplay } from '../utils/region';
import { getUserFacingError } from '../utils/errors';
import { needsEmailVerification, rememberPostVerifyPath } from '../utils/emailVerification';
import MarketingSurface from '../components/MarketingSurface';
import { marketingColor, marketingFont } from '../theme/marketingTokens';

export const LoginPage: React.FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedRegion, setSelectedRegion] = useState<AppRegion>(() => getCurrentRegion());
  const regionDisplay = useMemo(() => getRegionDisplay(selectedRegion), [selectedRegion]);

  // Sends the caller back where they came from — used by the invitation
  // accept page so signing in doesn't lose the invite token. Only a relative,
  // same-app path is honoured; anything else falls back to the dashboard.
  const postLoginDestination = () => {
    const redirect = searchParams.get('redirect');
    return redirect && /^\/[^/]/.test(redirect) ? redirect : '/app/dashboard';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      if (needsEmailVerification(auth.currentUser)) {
        rememberPostVerifyPath(postLoginDestination());
        navigate('/verify-email');
        return;
      }
      navigate(postLoginDestination());
    } catch (error) {
      setError(getUserFacingError(error, 'Failed to sign in. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
      if (needsEmailVerification(auth.currentUser)) {
        rememberPostVerifyPath(postLoginDestination());
        navigate('/verify-email');
        return;
      }
      navigate(postLoginDestination());
    } catch (error) {
      setError(getUserFacingError(error, 'Failed to sign in with Google. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setResetMessage('Please enter your email address');
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      setResetEmail('');
    } catch (error) {
      setResetMessage(getUserFacingError(error, 'Failed to send reset email. Please try again.'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetMessage('');
  };

  const handleRegionChange = (region: AppRegion) => {
    setSelectedRegion(region);
    const currentRegion = getCurrentRegion();
    if (region !== currentRegion) {
      window.location.assign(buildRegionLoginUrl(region));
    }
  };

  return (
    <MarketingSurface>
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper sx={{ padding: 4, width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="/logo-white.svg"
                  alt="Motzu Labs"
                  style={{
                    height: '28px',
                    width: 'auto',
                    maxWidth: '160px',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<span style="font-family: ${marketingFont.display}; font-weight: 700; font-size: 1.25rem; color: ${marketingColor.text};">Motzu Labs</span>`;
                    }
                  }}
                />
              </Box>
              <Chip
                label={`${regionDisplay.flag} ${regionDisplay.country} Portal`}
                color="primary"
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <Typography component="h1" variant="h5" gutterBottom>
                Sign In
              </Typography>
              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="region-select-label">Region</InputLabel>
                  <Select
                    labelId="region-select-label"
                    id="region-select"
                    value={selectedRegion}
                    label="Region"
                    onChange={(event) => handleRegionChange(event.target.value as AppRegion)}
                  >
                    <MenuItem value="us">🇺🇸 United States (motzulabs.com)</MenuItem>
                    <MenuItem value="ca">🇨🇦 Canada (motzulabs.ca)</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'login-email-input' } }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'login-password-input' } }}
                />
                <Box textAlign="right" sx={{ mt: 1 }}>
                  <Link 
                    component="button" 
                    variant="body2" 
                    onClick={() => setForgotPasswordOpen(true)}
                    sx={{ textDecoration: 'none' }}
                  >
                    Forgot your password?
                  </Link>
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
              
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    OR
                  </Typography>
                </Divider>
              
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  Sign in with Google
                </Button>
              
                <Box textAlign="center">
                  <Link component={RouterLink} to="/signup" variant="body2">
                    Don't have an account? Sign Up
                  </Link>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Forgot Password Dialog */}
        <Dialog open={forgotPasswordOpen} onClose={handleCloseForgotPassword} maxWidth="sm" fullWidth fullScreen={fullScreen}>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              disabled={resetLoading}
            />
            {resetMessage && (
              <Alert 
                severity={resetMessage.includes('sent') ? 'success' : 'error'} 
                sx={{ mt: 2 }}
              >
                {resetMessage}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForgotPassword} disabled={resetLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleForgotPassword} 
              variant="contained" 
              disabled={resetLoading || !resetEmail}
            >
              {resetLoading ? <CircularProgress size={24} /> : 'Send Reset Email'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MarketingSurface>
  );
};
